/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import { useAtomValue } from 'jotai'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import React from 'react'
import { MdPhone } from 'react-icons/md'
import { HeaderLanguageSwitch } from '@/components/e-commerce/header/HeaderLanguageSwitch'
import { HeaderLoginLink } from '@/components/e-commerce/header/HeaderLoginLink'
import { HeaderNavigationBarMenu } from '@/components/e-commerce/header/HeaderNavigationBarMenu'
import { HeaderRegionSelect } from '@/components/e-commerce/header/HeaderRegionSelect'
import { Link } from '@/i18n/navigation'
import { basketTotalQuantityAtom } from '@/store/basket'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type DesktopHeaderProps = {
  className?: string
}

/**
 * The header for the e-commerce website, containing the logo for MTa Learning, user/basket interactions, contact
 * information and the primary navigation menu.
 */
const DesktopHeader: React.FC<DesktopHeaderProps> = ({ className }) => {
  const t = useTranslations('DesktopHeader')
  const itemsInBasket = useAtomValue(basketTotalQuantityAtom)

  return (
    <header
      className={cn(
        'flex flex-row justify-between w-full bg-white px-5 sticky top-0 z-max',
        'border-b border-[#f0f0f0]',
        'border-b-3 border-primary-500',
        className,
      )}
      role={'banner'}
    >
      <Link href={'/'}>
        <Image src={'/images/mta-logo.svg'} className={'py-5'} width={143} height={86} alt={'MTa Learning Logo'} />
      </Link>
      <div className={'flex flex-col justify-between min-h-0'}>
        <div className={'flex flex-row gap-4 ml-auto'}>
          <div className={'flex flex-row gap-2 items-center'}>
            <MdPhone className={'text-white rounded-full bg-primary-500 text-2xl p-1'} />
            <a
              href={'tel:+441937844800'}
              className={'text-primary-500 font-bold text-base hover:underline hover:text-primary-600'}
            >
              INT +44 (0)1937 844800
            </a>
          </div>
          <div className={'flex flex-row gap-2 items-center'}>
            <MdPhone className={'text-white rounded-full bg-primary-500 text-2xl p-1'} />
            <a
              href={'tel:+12028882776'}
              className={'text-primary-500 font-bold text-base hover:underline hover:text-primary-600'}
            >
              US +1 202 888 2776
            </a>
          </div>
          <div
            className={cn(
              'flex flex-row gap-5 items-center bg-primary-500 rounded-bl-2xl rounded-br-2xl px-4 py-2',
              'text-xs uppercase text-white',
            )}
          >
            <div className={'flex flex-row gap-1 items-center'}>
              <Link href={'/basket'} className={'hover:underline'}>
                {t('basket')}
              </Link>
              {itemsInBasket > 0 && (
                <span className={'flex items-center justify-center rounded-full bg-amber-400 text-black w-5 h-5'}>
                  {itemsInBasket}
                </span>
              )}
            </div>
            <HeaderLoginLink />
            <HeaderRegionSelect />
          </div>
          <HeaderLanguageSwitch />
        </div>
        <HeaderNavigationBarMenu />
      </div>
    </header>
  )
}

export { DesktopHeader }
